<script setup lang="ts">
import { computed, reactive, ref } from "vue";
import type { ComputedRef } from "vue";
import type {
  Scenario,
  XrefHousing,
  XrefPump
} from "@/composables/forms/formTypes";
import type { ScenarioCalculations } from "@/composables/scenarioCalculations/calculationTypes";

import SchematicCalcTable from "./SchematicCalcTable.vue";
import SchematicSurfaceTable from "./SchematicSurfaceTable.vue";
import SchematicEquipmentTable from "./SchematicEquipmentTable.vue";
import SchematicFluidRates from "./SchematicFluidRates.vue";

const debug = ref(false);

const schematicWidth = 850;
const schematicHeight = 900;

const casingTopMargin = 16;
const casingBottomMargin = 40;
const equipmentWidth = 32;
const equipmentXOffset = 565;
const equipmentYOffset = 120;
const infoBoxWidth = 480;
const smallBoxWidth = 240;
const minGraphicalStages = 75;
const infoboxXOffset = 32;

// Casing needs to be wider when YTool is present to accommodate all equipment
const casingWidth = computed(() => {
  if (props.scenario?.equipment?.use_uphole_y_tool) {
    return 190; // Wider casing to fit bypass tubing and pump stack with clearance
  }
  return 64; // Default casing width
});

const casingXOffset = computed(
  () => equipmentXOffset - (casingWidth.value - equipmentWidth) / 2
);

const smallBoxXOffset = computed(() => equipmentXOffset + equipmentWidth + 40);

// When ytool is present, shift equipment to align with right outlet
const pumpXOffset = computed(() =>
  props.scenario?.equipment?.use_uphole_y_tool
    ? equipmentXOffset + equipmentWidth * 0.6
    : equipmentXOffset
);

const props = defineProps<{
  scenario: Scenario | undefined | null;
  scenarioCalculations: ScenarioCalculations;
  sagdMode?: boolean;
}>();

// eslint-disable-next-line vue/no-setup-props-destructure
const {
  surfaceFlowRates,
  descendingOrderPumps,
  orderedHousings,
  orderedCables,
  descendingOrderSeals,
  orderedIntakes
} = props.scenarioCalculations;

const descendingPumpsWithHousingIndex: ComputedRef<
  { pump?: XrefPump; housing?: XrefHousing; housingIndex: number }[]
> = computed(() => {
  if (props.scenarioCalculations.descendingPumpsWithHousingIndex.value.length) {
    return props.scenarioCalculations.descendingPumpsWithHousingIndex.value;
  }
  return [{ housing: undefined, pump: undefined, housingIndex: 0 }];
});

const validHousingIndexesWithoutHousing = computed(() => {
  return descendingPumpsWithHousingIndex.value
    .filter(v => {
      return !v.housing && v.pump;
    })
    .map(v => v.housingIndex);
});

// TODO: make these based on physical part lengths, instead of generic?
const heights = reactive({
  stage: 0.4,
  coupling: 30,
  boltOnDischarge: 20,
  seal: 30,
  intake: 30,
  motor: 50,
  sensor: 20,
  bmb: 20,
  shroudPastSensor: 10, // how far a shroud, if present, extends beyond the sensor/motor
  ytool: 30,
  bypassTubingPerFoot: 1.0 // pixels per foot of tubing length
});

// #region layout
const rawEquipmentHeight = computed(
  (): number =>
    casingTopMargin +
    casingBottomMargin +
    // gear that is 1:1 per scenario
    heights.coupling +
    heights.motor +
    // for dynamic scaling, we count the stages in each housing. If there's no housing
    // or a competitor pump (also no housings), then we just draw 1 housing
    heights.stage *
      (orderedHousings?.value
        ? orderedHousings?.value?.reduce(
            (sum, housing) =>
              sum + (housing?.stage_count || minGraphicalStages),
            0
          )
        : minGraphicalStages) +
    // these are N:1 but are all the same height, we default to length of 1 to draw "ghost" equipment
    heights.intake * (orderedIntakes?.value?.length || 1) +
    heights.seal * (descendingOrderSeals?.value?.length || 1) +
    // optional items
    (props.sagdMode
      ? heights.bmb
      : props.scenario?.equipment?.r_sensor
        ? heights.sensor
        : 0) +
    (props.scenario?.equipment?.r_shroud?.interior_diameter_inches
      ? heights.shroudPastSensor
      : 0) +
    // bolt-on discharge if present
    (props.scenario?.equipment?.r_bolt_on_discharge
      ? heights.boltOnDischarge
      : 0) +
    // ytool if present
    (props.scenario?.equipment?.use_uphole_y_tool ? heights.ytool : 0)
);

const scale = computed(
  (): number =>
    (schematicHeight - equipmentYOffset - casingBottomMargin) /
    rawEquipmentHeight.value
);

const cumulativeSum = (sum: number) => (n: number) => (sum += n);

const getPumpHousingHeight = ({
  pump,
  housing
}: {
  pump?: XrefPump;
  housing?: XrefHousing;
}) => {
  return (
    Math.max(
      housing
        ? housing.stage_count || minGraphicalStages
        : pump?.total_stages || minGraphicalStages,
      minGraphicalStages
    ) *
    heights.stage *
    scale.value
  );
};

const scaledHousingHeights = computed(() =>
  descendingPumpsWithHousingIndex.value
    .map(getPumpHousingHeight)
    .map(cumulativeSum(0))
);

// these are computed Y coordinates for the ends of types of gear
const bottomCoupling = computed(
  () => equipmentYOffset + heights.coupling * scale.value
);
const bottomYTool = computed(() =>
  props.scenario?.equipment?.use_uphole_y_tool
    ? bottomCoupling.value + heights.ytool * scale.value - 8
    : bottomCoupling.value
);
const bottomBoltOnDischarge = computed(() =>
  props.scenario?.equipment?.r_bolt_on_discharge
    ? bottomYTool.value + heights.boltOnDischarge * scale.value
    : bottomYTool.value
);
const bottomHousings = computed(
  () =>
    bottomBoltOnDischarge.value +
    (scaledHousingHeights.value?.length
      ? scaledHousingHeights.value[scaledHousingHeights.value.length - 1]
      : heights.stage * minGraphicalStages * scale.value)
);
const bottomIntakes = computed(
  () =>
    bottomHousings.value +
    (orderedIntakes?.value?.length || 1) * heights.intake * scale.value
);
const bottomSeals = computed(
  () =>
    bottomIntakes.value +
    (descendingOrderSeals?.value?.length || 1) * heights.seal * scale.value
);
const bottomMotor = computed(
  () => bottomSeals.value + heights.motor * scale.value
);
const bottomSensorOrBMB = computed(() =>
  props.sagdMode
    ? bottomMotor.value + heights.bmb * scale.value
    : props.scenario?.equipment?.r_sensor
      ? bottomMotor.value + heights.sensor * scale.value
      : bottomMotor.value
);
const bottomCasing = computed(() => schematicHeight - casingBottomMargin);
// #endregion layout

const shroudPath = computed(() => {
  /* makes the following shape...
    L0  / \  L4
       |   | L3
       |   |
    L1 |___| L2

    When Y-tool is enabled, cut off left half and make narrower to fit pump only
  */

  if (props.scenario?.equipment?.use_uphole_y_tool) {
    // Shroud cut to align with right edge of bypass tubing
    const shroudWidth = equipmentWidth * 1.2;
    const neckWidth = shroudWidth * 0.65;
    const xPos = pumpXOffset.value - (shroudWidth - equipmentWidth) / 2;
    const leftCutPoint = equipmentXOffset + equipmentWidth * 0.475;

    return `
      M ${leftCutPoint} ${bottomHousings.value - 10}
      L ${leftCutPoint} ${bottomHousings.value}
      L ${leftCutPoint} ${bottomSensorOrBMB.value + heights.shroudPastSensor}
      L ${xPos + shroudWidth} ${
        bottomSensorOrBMB.value + heights.shroudPastSensor
      }
      L ${xPos + shroudWidth} ${bottomHousings.value}
      L ${xPos + (shroudWidth - neckWidth) * 0.5 + neckWidth} ${
        bottomHousings.value - 10
      }
      z`;
  }

  // Original shroud for non-Y-tool configuration
  const shroudWidth =
    equipmentWidth + (casingWidth.value - equipmentWidth) * 0.5;
  const gap = (casingWidth.value - shroudWidth) / 2;
  const neckWidth = shroudWidth * 0.65;
  const xPos =
    pumpXOffset.value - (casingWidth.value - equipmentWidth) / 2 + gap;

  return `
    M ${xPos + (shroudWidth - neckWidth) * 0.5} ${bottomHousings.value - 10}
    L ${xPos} ${bottomHousings.value}
    L ${xPos} ${bottomSensorOrBMB.value + heights.shroudPastSensor}
    L ${xPos + shroudWidth} ${
      bottomSensorOrBMB.value + heights.shroudPastSensor
    }
    L ${xPos + shroudWidth} ${bottomHousings.value}
    L ${xPos + (shroudWidth - neckWidth) * 0.5 + neckWidth} ${
      bottomHousings.value - 10
    }
    z`;
});
</script>

<template>
  <svg
    v-if="scenario"
    :key="`schematic-${scenario.id}`"
    class="schematic"
    :viewBox="`0 0 ${schematicWidth} ${schematicHeight}`"
  >
    <defs>
      <pattern
        id="diag"
        patternUnits="userSpaceOnUse"
        :patternTransform="`rotate(66) scale(1)`"
        width="4"
        height="4"
      >
        <rect x="0" y="0" width="4" height="4" fill="var(--gray-800)" />
        <path
          d="M-1,1 l2,-2 M0,4 l4,-4 M3,5 l2,-2"
          stroke="var(--gray-100)"
          :stroke-width="1"
        />
      </pattern>

      <linearGradient id="shroud-fill">
        <stop class="s1" />
        <stop class="s2" />
        <stop class="s3" />
      </linearGradient>

      <linearGradient id="water-cut-fill">
        <!-- CSS vars generated from $colors in _variables.scss via _colors.scss -->
        <stop stop-color="var(--red-500)" offset="0%" />
        <stop
          stop-color="var(--red-500)"
          :offset="`${100 - 100 * surfaceFlowRates?.waterCutRatio}%`"
        />
        <stop
          stop-color="var(--gray-500)"
          :offset="`${100 - 100 * (surfaceFlowRates?.waterCutRatio + 0.0001)}%`"
        />
        <stop stop-color="var(--gray-500)" offset="100%" />
      </linearGradient>
    </defs>

    <g class="graphics">
      <g class="surface" transform="translate(110 -40)">
        <image
          :class="{
            unset: !scenario.equipment?.use_vsd || !scenario.equipment?.r_vsd
          }"
          :x="-100"
          :y="-2"
          :height="160"
          href="/img/schematic/vsd.png"
        />
        <image
          :x="431"
          :y="0"
          :width="180"
          :height="160"
          href="/img/schematic/wellhead.png"
        />
      </g>

      <g class="borehole">
        <rect
          :x="casingXOffset"
          :y="equipmentYOffset"
          :height="schematicHeight - equipmentYOffset - casingBottomMargin"
          :width="casingWidth"
          class="casing"
        />
        <g class="perfs">
          <line
            :x1="casingXOffset"
            :x2="casingXOffset"
            :y1="bottomCasing - 100"
            :y2="bottomCasing"
          />
          <line
            :x1="casingXOffset + casingWidth"
            :x2="casingXOffset + casingWidth"
            :y1="bottomCasing - 100"
            :y2="bottomCasing"
          />
        </g>
      </g>

      <image
        class="grass"
        x="0"
        y="105"
        preserveAspectRatio="none"
        :width="schematicWidth"
        height="15"
        href="/img/schematic/grass.svg"
      />

      <g
        v-if="scenario.equipment?.r_shroud?.interior_diameter_inches"
        class="shroud"
      >
        <path :d="shroudPath" />
      </g>

      <!-- #region coupling -->
      <g class="coupling">
        <image
          preserveAspectRatio="none"
          :x="equipmentXOffset"
          :y="equipmentYOffset"
          :width="equipmentWidth"
          :height="heights.coupling * scale"
          href="/img/schematic/coupling.svg"
        />
      </g>
      <!-- #endregion coupling-->

      <!-- #region bypass-tubing -->
      <g
        v-if="
          scenario.equipment?.use_uphole_y_tool &&
          scenarioCalculations.orderedYToolBypassTubings.value[0]
        "
        class="bypass-tubing"
      >
        <image
          :x="equipmentXOffset + equipmentWidth * 0.1"
          :y="bottomYTool"
          :width="equipmentWidth * 0.375"
          :height="bottomSensorOrBMB - bottomYTool"
          preserveAspectRatio="none"
          href="/img/schematic/connector.svg"
        />
      </g>
      <!-- #endregion bypass-tubing-->

      <!-- #region ytool -->
      <g v-if="scenario.equipment?.use_uphole_y_tool" class="ytool">
        <image
          :x="equipmentXOffset - equipmentWidth * 0.825"
          :y="bottomCoupling - 8"
          :width="equipmentWidth * 2.5"
          :height="heights.ytool * scale"
          preserveAspectRatio="none"
          href="/img/schematic/ytool.svg"
        />
      </g>
      <!-- #endregion ytool-->

      <!-- #region cables -->
      <g class="cable">
        <path
          v-if="scenario.equipment?.use_uphole_y_tool"
          :class="{ unset: !orderedCables?.length }"
          :d="`
              M ${equipmentXOffset + equipmentWidth * 0.5} ${equipmentYOffset}
              L ${equipmentXOffset + equipmentWidth * 0.5} ${bottomSeals}
              L ${pumpXOffset + equipmentWidth * 0.5} ${bottomSeals + 40}
            `"
        />
        <path
          v-else
          :class="{ unset: !orderedCables?.length }"
          :d="`
              M ${equipmentXOffset + equipmentWidth + 3} ${equipmentYOffset}
              L ${equipmentXOffset + equipmentWidth + 3} ${bottomCoupling}
              Q ${equipmentXOffset + equipmentWidth + 3} ${bottomCoupling + 10}, ${(equipmentXOffset + pumpXOffset) / 2 + equipmentWidth + 3} ${bottomCoupling + 15}
              Q ${pumpXOffset + equipmentWidth + 3} ${bottomCoupling + 20}, ${pumpXOffset + equipmentWidth + 3} ${bottomCoupling + 30}
              L ${pumpXOffset + equipmentWidth + 3} ${bottomSeals}
              L ${pumpXOffset + equipmentWidth * 0.5} ${bottomSeals + 40}
            `"
        />
      </g>
      <!-- #endregion cables -->

      <!-- #region bolt-on-discharge -->
      <g
        v-if="scenario.equipment?.r_bolt_on_discharge"
        class="bolt-on-discharge"
      >
        <image
          :x="pumpXOffset"
          :y="bottomYTool"
          :width="equipmentWidth"
          :height="heights.boltOnDischarge * scale"
          preserveAspectRatio="none"
          href="/img/schematic/bolt-on-discharge.svg"
        />
        <text
          :x="pumpXOffset + equipmentWidth * 0.5"
          :y="bottomYTool + heights.boltOnDischarge * scale * 0.5"
          v-text="'BOD'"
        />
      </g>
      <!-- #endregion bolt-on-discharge-->

      <!-- #region housings -->
      <g class="housings">
        <g
          v-for="(
            { pump, housing, housingIndex }, i
          ) in descendingPumpsWithHousingIndex"
          :key="`housing-${i}`"
          class="housing"
        >
          <image
            :class="{
              unset:
                housing === null &&
                !validHousingIndexesWithoutHousing.includes(housingIndex)
            }"
            :x="pumpXOffset"
            :y="
              bottomBoltOnDischarge + (i > 0 ? scaledHousingHeights[i - 1] : 0)
            "
            :width="equipmentWidth"
            :height="getPumpHousingHeight({ pump, housing })"
            preserveAspectRatio="none"
            href="/img/schematic/pump.svg"
          />
          <text
            v-if="(descendingOrderPumps?.length ?? 0) > 0"
            :x="pumpXOffset + equipmentWidth * 0.5"
            :y="
              bottomBoltOnDischarge +
              (i > 0 ? scaledHousingHeights[i - 1] : 0) +
              getPumpHousingHeight({ pump, housing }) * 0.5
            "
            v-text="`P${housingIndex + 1}`"
          />
        </g>
      </g>
      <!-- #endregion housings-->

      <!-- #region intakes -->
      <g class="intakes">
        <g
          v-for="(n, i) in orderedIntakes.length ? orderedIntakes : [null]"
          :key="`intake-${i}`"
          class="intake"
        >
          <image
            :class="{ unset: n === null }"
            :x="pumpXOffset"
            :y="bottomHousings + i * heights.intake * scale"
            :width="equipmentWidth"
            :height="heights.intake * scale"
            preserveAspectRatio="none"
            href="/img/schematic/intake.svg"
          />
          <text
            v-if="n !== null"
            :x="pumpXOffset + equipmentWidth * 0.5"
            :y="
              bottomHousings +
              i * heights.intake * scale +
              heights.intake * scale * 0.5
            "
            v-text="`I${i + 1}`"
          />
        </g>
      </g>
      <!-- #endregion intakes -->

      <!-- #region seals -->
      <g class="seals">
        <g
          v-for="(s, i) in descendingOrderSeals.length
            ? descendingOrderSeals
            : [null]"
          :key="`seal-${i}`"
          class="seal"
        >
          <image
            :class="{ unset: s === null }"
            :x="pumpXOffset"
            :y="bottomIntakes + i * heights.seal * scale"
            :width="equipmentWidth"
            :height="heights.seal * scale"
            preserveAspectRatio="none"
            href="/img/schematic/pump.svg"
          />
          <text
            v-if="s !== null"
            :x="pumpXOffset + equipmentWidth * 0.5"
            :y="bottomIntakes + i * heights.seal * scale + heights.seal * 0.5"
            v-text="`S${i + 1}`"
          />
        </g>
      </g>
      <!-- #endregion seals -->

      <!-- #region motor -->
      <g class="motor">
        <image
          :class="{ unset: !scenario.equipment?.r_motor }"
          :x="pumpXOffset"
          :y="bottomSeals"
          :width="equipmentWidth"
          :height="heights.motor * scale"
          preserveAspectRatio="none"
          href="/img/schematic/motor.svg"
        />
      </g>
      <!-- #endregion motor-->

      <!-- #region sensor/bmb -->
      <g v-if="sagdMode" class="sensor">
        <image
          :x="pumpXOffset"
          :y="bottomMotor"
          :width="equipmentWidth"
          :height="heights.bmb * scale"
          href="/img/schematic/bmb.svg"
        />
      </g>
      <g v-else-if="scenario.equipment?.r_sensor" class="sensor">
        <image
          :x="pumpXOffset"
          :y="bottomMotor"
          :width="equipmentWidth"
          :height="heights.sensor * scale"
          href="/img/schematic/sensor.svg"
        />
      </g>
      <!-- #endregion sensor/bmb -->
    </g>

    <g class="tables">
      <!-- #region title-box -->
      <foreignObject x="140" y="-44" :width="340" height="160">
        <span class="title">
          Design Schematic <span class="lighter">{{ scenario?.title }}</span>
        </span>
      </foreignObject>
      <!-- #endregion title-->

      <!-- #region surface-box -->
      <foreignObject x="140" y="-14" :width="380" height="160">
        <SchematicSurfaceTable
          :scenario="scenario"
          :scenario-calculations="scenarioCalculations"
          :sagdMode="sagdMode"
        />
      </foreignObject>
      <!-- #endregion surface -->

      <foreignObject
        :x="infoboxXOffset"
        :y="equipmentYOffset + 16"
        :width="infoBoxWidth"
        :height="schematicHeight - equipmentYOffset"
      >
        <SchematicEquipmentTable
          :scenario="scenario"
          :scenario-calculations="scenarioCalculations"
          :sagdMode="sagdMode"
        />
      </foreignObject>

      <SchematicFluidRates
        :scenario="scenario"
        :scenario-calculations="scenarioCalculations"
        :sagdMode="sagdMode"
        :transform="`translate(${smallBoxXOffset} -32)`"
      />

      <foreignObject
        :x="smallBoxXOffset"
        :y="equipmentYOffset + 16"
        :width="smallBoxWidth"
        :height="schematicHeight - equipmentYOffset"
      >
        <SchematicCalcTable
          :scenario="scenario"
          :scenario-calculations="scenarioCalculations"
          :sagdMode="sagdMode"
        />
      </foreignObject>
    </g>

    <g v-if="debug" class="debug">
      <line
        :x1="casingXOffset"
        :x2="casingXOffset"
        :y1="0"
        :y2="schematicHeight"
        stroke="orchid"
      />
      <line
        :x1="equipmentXOffset"
        :x2="equipmentXOffset"
        :y1="0"
        :y2="schematicHeight"
        stroke="aqua"
      />
      <line
        :x1="equipmentXOffset + equipmentWidth"
        :x2="equipmentXOffset + equipmentWidth"
        :y1="0"
        :y2="schematicHeight"
        stroke="orange"
      />
      <line
        :x1="casingXOffset + casingWidth"
        :x2="casingXOffset + casingWidth"
        :y1="0"
        :y2="schematicHeight"
        stroke="red"
      />
      <line
        :x1="0"
        :x2="schematicWidth"
        :y1="equipmentYOffset"
        :y2="equipmentYOffset"
      />
      <line
        :x1="0"
        :x2="schematicWidth"
        :y1="bottomCoupling"
        :y2="bottomCoupling"
      />
      <line
        :x1="0"
        :x2="schematicWidth"
        :y1="bottomHousings"
        :y2="bottomHousings"
      />
      <line
        :x1="0"
        :x2="schematicWidth"
        :y1="bottomIntakes"
        :y2="bottomIntakes"
      />
      <line
        stroke="purple"
        :x1="0"
        :x2="schematicWidth"
        :y1="bottomSeals"
        :y2="bottomSeals"
      />
      <line
        stroke="red"
        :x1="0"
        :x2="schematicWidth"
        :y1="bottomMotor"
        :y2="bottomMotor"
      />
      <line
        stroke="orange"
        :x1="0"
        :x2="schematicWidth"
        :y1="bottomSensorOrBMB"
        :y2="bottomSensorOrBMB"
      />
    </g>
  </svg>
</template>

<style lang="scss">
.debug {
  line {
    // stroke: var(--info-500);
    stroke-dasharray: 12px 3px 4px 3px;
    stroke-width: 1px;
  }
}
#shroud-fill {
  .s1 {
    offset: 5%;
    stop-opacity: 0.85;
    stop-color: var(--surface-400);
  }
  .s2 {
    offset: 50%;
    stop-opacity: 0.85;
    stop-color: var(--gray-800);
  }
  .s3 {
    offset: 95%;
    stop-opacity: 0.85;
    stop-color: var(--surface-400);
  }
}

.schematic {
  aspect-ratio: 8.5 / 11; // portrait letter
  display: block;
  font-size: 7pt;
  max-height: 90vh;
  break-after: page;

  // max-width: 90vw;

  .title {
    font-size: 12.5px;
    color: var(--gray-900);
    font-weight: 300;
    .lighter {
      font-weight: 600;
      color: var(--danger-500);
    }
  }

  .casing {
    stroke: var(--gray-600);
    stroke-width: 2;
    fill: var(--gray-300);
  }
  .perfs {
    line {
      stroke: var(--gray-300);
      stroke-dasharray: 4px 6px;
      stroke-width: 4;
    }
  }

  .shroud {
    path {
      stroke: var(--gray-500);
      stroke-width: 1px;
      fill: url(#shroud-fill);
    }
  }

  .cable,
  .mle {
    stroke: url(#diag);
    stroke-opacity: 1;
    stroke-width: 4;
    stroke-linecap: round;
    fill: none;
  }

  .unset {
    filter: contrast(0.05) opacity(0.5);
  }

  .grass {
    filter: invert(1) opacity(0.3);
  }

  .surface {
    image:not(.unset) {
      filter: contrast(0.55) opacity(0.3);
    }
  }

  .bolt-on-discharge,
  .housings,
  .intakes,
  .seals {
    text {
      fill: white;
      font-size: 10pt;
      font-weight: 400;
      font-family: var(--monospace-family);
      dominant-baseline: middle;
      text-anchor: middle;
    }
  }

  .fluid-rates {
    fill: var(--text-color);

    .title {
      font-size: 12pt;
      text-anchor: middle;
    }
    .totalLiquidRate {
      text-anchor: middle;
      font-family: var(--monospace-family);
      font-size: 16pt;
      letter-spacing: -1pt;
    }

    .bar {
      rect {
        stroke: var(--border-color);
        stroke-width: 1px;
        fill: url(#water-cut-fill);
      }
      .label {
        font-size: 12pt;
        font-weight: 400;
        dominant-baseline: middle;
        text-anchor: start;
        &.water {
          text-anchor: end;
        }
      }
      .oilRate {
        dominant-baseline: middle;
        font-family: var(--monospace-family);
        font-size: 12pt;
      }
      .waterRate {
        dominant-baseline: middle;
        text-anchor: end;
        font-family: var(--monospace-family);
        font-size: 12pt;
      }
    }
  }

  .info-box {
    width: 100%;
    table-layout: fixed;
    border-collapse: collapse;
    // background: var(--surface-200);

    thead {
      tr {
        background: var(--surface-hover);
        font-weight: 800;
        font-family: var(--monospace-family);
        th {
          text-align: right;
          font-size: 9pt;
          padding: 0.25em;
        }
        th:nth-child(1) {
          text-align: left;
          width: 82%;
        }
      }
    }

    tbody {
      tr {
        border-bottom: 1px solid var(--gray-200);
      }

      th {
        text-align: left;
        width: 20%;
        padding: 0.25em;
      }

      td {
        vertical-align: top;
        padding: 0.25em;

        &:nth-child(1) {
          width: 85%;
        }

        &:nth-child(2),
        &:nth-child(3) {
          max-width: min-content;
          text-align: right;
          font-family: var(--monospace-family);
        }
      }
    }
  }

  .right-box {
    width: 100%;
    table-layout: fixed;
    border-collapse: collapse;

    tr {
      td {
        h4 {
          margin-top: 0.5em;
          margin-bottom: 0.125em;
          font-size: 8pt;
          font-family: var(--font-family);
          font-weight: 400;
        }
        padding: 0.15em 0;
        padding-left: 12pt;
        font-family: var(--monospace-family);
        font-weight: 800;
        font-size: 8.5pt;
      }
    }
  }

  .right-box-flex,
  .info-box-flex {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    min-height: 100%;
    padding-bottom: 4em;
    gap: 0pt;
  }
}
[data-theme="dark"] {
  .grass {
    filter: opacity(0.3);
  }
}
</style>
